import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ApprovableData } from '@/types/api';

const CLIENT_KEY = (clientId: number, page: number) => ['approvables', 'client', clientId, page];

export function useApprovables(clientId: number | undefined, page = 1) {
  return useQuery({
    queryKey: clientId == null ? ['approvables', 'client', null] : CLIENT_KEY(clientId, page),
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<ApprovableData>> => {
      const { data } = await api.get<Paginated<ApprovableData>>(
        `/v1/clients/${clientId}/approvables`,
        { params: { page } },
      );
      return data;
    },
  });
}

export function useAllApprovables(filters: { q?: string; type?: string; status?: string; page?: number } = {}) {
  return useQuery({
    queryKey: ['approvables', 'all', filters],
    queryFn: async (): Promise<Paginated<ApprovableData>> => {
      const { data } = await api.get<Paginated<ApprovableData>>('/v1/approvables', { params: filters });
      return data;
    },
  });
}

export function useOpenApprovablesCount() {
  return useQuery({
    queryKey: ['approvables', 'open-count'],
    queryFn: async (): Promise<number> => {
      const { data } = await api.get<{ count: number }>('/v1/approvables/open-count');
      return data.count;
    },
  });
}

export type ApprovableUploadInput = {
  type: 'proposal' | 'hardware_auth';
  title: string;
  description?: string;
  total_cents?: number;
  deposit_cents?: number;
  expires_at?: string;
  file: File;
};

export function useUpdateApprovable(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: {
        title?: string;
        description?: string;
        total_cents?: number | null;
        deposit_cents?: number | null;
      };
    }): Promise<ApprovableData> => {
      await primeCsrf();
      const { data } = await api.patch<ApprovableData>(
        `/v1/clients/${clientId}/approvables/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvables', 'client', clientId] }),
  });
}

export function useUploadApprovable(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApprovableUploadInput): Promise<ApprovableData> => {
      await primeCsrf();
      const fd = new FormData();
      fd.append('type', input.type);
      fd.append('title', input.title);
      if (input.description) fd.append('description', input.description);
      if (input.total_cents != null) fd.append('total_cents', String(input.total_cents));
      if (input.deposit_cents != null) fd.append('deposit_cents', String(input.deposit_cents));
      if (input.expires_at) fd.append('expires_at', input.expires_at);
      fd.append('file', input.file);

      const { data } = await api.post<ApprovableData>(
        `/v1/clients/${clientId}/approvables`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvables', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
      qc.invalidateQueries({ queryKey: ['approvables', clientId] });
    },
  });
}

export type SendApprovableInput = {
  approvableId: number;
  /** The admin's wording from the composer; blank keeps the standard text. */
  subject?: string;
  message?: string;
  /** Chosen in the send dialog. Omitted, the API falls back to the address
   * captured on the approvable. */
  to?: string[];
  cc?: string[];
};

export function useSendApprovable(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendApprovableInput): Promise<ApprovableData> => {
      await primeCsrf();
      const body: Record<string, unknown> = {};
      if (input.subject) body.subject = input.subject;
      if (input.message) body.message = input.message;
      if (input.to) body.to = input.to;
      if (input.cc) body.cc = input.cc;
      const { data } = await api.post<ApprovableData>(
        `/v1/clients/${clientId}/approvables/${input.approvableId}/send`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvables', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
      qc.invalidateQueries({ queryKey: ['approvables', clientId] });
    },
  });
}

export function useRemindApprovable(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendApprovableInput): Promise<ApprovableData> => {
      await primeCsrf();
      const body: Record<string, unknown> = {};
      if (input.subject) body.subject = input.subject;
      if (input.message) body.message = input.message;
      if (input.to) body.to = input.to;
      if (input.cc) body.cc = input.cc;
      const { data } = await api.post<ApprovableData>(
        `/v1/clients/${clientId}/approvables/${input.approvableId}/remind`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvables', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
      qc.invalidateQueries({ queryKey: ['approvables', clientId] });
    },
  });
}

/** Admin-side "put on hold": park a sent proposal without waiting on the client. */
export function useDeferApprovable(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (approvableId: number): Promise<ApprovableData> => {
      await primeCsrf();
      const { data } = await api.post<ApprovableData>(
        `/v1/clients/${clientId}/approvables/${approvableId}/defer`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvables', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
      qc.invalidateQueries({ queryKey: ['approvables', clientId] });
    },
  });
}

export function useDeleteApprovable(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (approvableId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/approvables/${approvableId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvables'] }),
  });
}

/**
 * Confirmation copy for deleting an approvable, shared by the client tab and
 * the cross-client list. One that was already signed says so explicitly, since
 * the approval record and any payment outlive the approvable itself.
 */
export function approvableDeleteWarning(approvable: Pick<ApprovableData, 'status'>): string {
  const base =
    'It disappears from every list, the client portal, and its public approval link.';
  const signed = ['approved', 'paid', 'declined'].includes(approvable.status)
    ? ' The signed record and any payment stay on file.'
    : '';

  return `${base}${signed} This cannot be undone from the app.`;
}

/**
 * Delete from the cross-client list, where each row carries its own client and
 * a hook can't be bound to one up front.
 */
export function useDeleteAnyApprovable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clientId,
      approvableId,
    }: {
      clientId: number;
      approvableId: number;
    }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/approvables/${approvableId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvables'] }),
  });
}
